import { createContext, useContext, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useConfig } from "../config/ConfigProvider";
import { createApiClient } from "./client";
import type { ApiClient } from "./client";

const ApiContext = createContext<ApiClient | null>(null);

/**
 * Creates a single API client wired to the current config and the live Cognito
 * access token. The token getter is read through a ref so refreshed tokens are picked up
 * without rebuilding the client (and without re-rendering consumers).
 */
export function ApiProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const config = useConfig();

  const tokenRef = useRef(auth.controller.getAccessToken);
  tokenRef.current = auth.controller.getAccessToken;

  const client = useMemo(
    () => createApiClient(config.apiBaseUrl, () => tokenRef.current(), auth.controller.onUnauthorized),
    [config.apiBaseUrl, auth.controller],
  );

  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

/** Access the shared API client. Throws if used outside an ApiProvider. */
export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return client;
}
